import { useEffect, useRef, useState } from 'react'
import { useBlocker, useLocation } from 'react-router-dom'
import Modal from '@components/shared/Modal'
import PecasAcervo from '@services/pecasAcervo'
import useAcervoCatalog from './useAcervoCatalog'
import useLegalResearch from './useLegalResearch'
import LegalResearchPanel from './LegalResearchPanel'
import useJuditDocuments from './useJuditDocuments'
import JuditDocumentsPanel from './JuditDocumentsPanel'
import JuditDocumentSources from './JuditDocumentSources'
import DraftGenerationUsage from './DraftGenerationUsage'
import { resolveJuditProcess } from './juditDocumentHelpers'
import { researchWarning } from './legalResearchHelpers'
import { actionLabel, areaLabel, isCanonicalAction } from './acervoHelpers'
import './pecasAcervo.css'
import './panaceiaTest.css'

const MAX_PUBLICATION_CHARACTERS = 10000
const MAX_GUIDANCE_CHARACTERS = 2000
const DEFAULT_GENERATION_PARAMETERS = { temperatura: '0.2', topP: '0.9' }
const CASE_FIELDS = [
  { key: 'parteRepresentada', label: 'Cliente / parte representada', limit: 240 },
  { key: 'poloProcessual', label: 'Polo processual do cliente', limit: 80 },
  { key: 'advogadoResponsavel', label: 'Advogado responsável', limit: 240 },
  { key: 'oabResponsavel', label: 'OAB do responsável', limit: 40 },
  { key: 'tribunalOrigem', label: 'Tribunal de origem', limit: 240 },
]
const DECISION_TYPES = [
  { value: 'ACORDAO_PRINCIPAL', label: 'Acórdão principal' },
  { value: 'ACORDAO_INTEGRATIVO', label: 'Acórdão integrativo / embargos de declaração' },
  { value: 'DECISAO_PRINCIPAL', label: 'Sentença ou outra decisão principal' },
  { value: 'DECISAO_INTEGRATIVA', label: 'Decisão integrativa / embargos' },
]
const EMPTY_CASE_FIELDS = Object.fromEntries(CASE_FIELDS.map(({ key }) => [key, '']))

export default function PanaceiaTestPanel() {
  const location = useLocation()
  const processContext = location.state?.processoContexto
  const [fields, setFields] = useState({ area: '', acao: '', publicacao: '', orientacoes: '',
    ...EMPTY_CASE_FIELDS, naturezaDecisao: '', acordaoPrincipal: '', ...DEFAULT_GENERATION_PARAMETERS,
    provedorGeracao: 'ollama', modeloGeracao: '' })
  const [catalogRevision, setCatalogRevision] = useState(0)
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [copyNotice, setCopyNotice] = useState('')
  const [pendingExit, setPendingExit] = useState(null)
  const [useResearch, setUseResearch] = useState(false)
  const [useDocuments, setUseDocuments] = useState(Boolean(processContext?.buscarDocumentos))
  const [providers, setProviders] = useState([])
  const [providerError, setProviderError] = useState('')
  const [generationAudit, setGenerationAudit] = useState(null)
  const [attemptInput, setAttemptInput] = useState(null)
  const [costUncertain, setCostUncertain] = useState(false)
  const operation = useRef(null)
  const catalog = useAcervoCatalog('', catalogRevision)
  const process = resolveJuditProcess(fields.publicacao, processContext?.numeroProcesso || '')
  const judit = useJuditDocuments(fields.publicacao, process.numeroProcesso)
  const caseFields = { ...fields, ...(useDocuments ? judit.context : {}) }
  const research = useLegalResearch(caseFields)
  const hasJudit = useDocuments && Boolean(judit.consulta)
  const juditBlocked = useDocuments && (!hasJudit || Boolean(process.error) || judit.blocked)
  const isClaude = fields.provedorGeracao === 'claude'
  const selectedProvider = providers.find((provider) => provider.provider === fields.provedorGeracao)
  const generationReady = !isClaude || Boolean(selectedProvider?.available && selectedProvider.allowedModels?.includes(fields.modeloGeracao))
  const temperature = Number(fields.temperatura)
  const topP = Number(fields.topP)
  const validTemperature = fields.temperatura.trim() !== '' && Number.isFinite(temperature) && temperature >= 0 && temperature <= 2
  const validTopP = fields.topP.trim() !== '' && Number.isFinite(topP) && topP >= 0.01 && topP <= 1
  const validRepresentation = Boolean(fields.advogadoResponsavel.trim()) === Boolean(fields.oabResponsavel.trim())
  const hasCaseData = CASE_FIELDS.some(({ key }) => fields[key].trim()) || fields.naturezaDecisao || fields.acordaoPrincipal.trim()
  const changedParameters = Object.entries(DEFAULT_GENERATION_PARAMETERS).some(([key, value]) => fields[key] !== value)
  const hasWork = Boolean(fields.publicacao.trim() || fields.orientacoes.trim() || hasCaseData || changedParameters || result || busy || research.job || research.phase || useDocuments && judit.hasWork || isClaude)
  const valid = catalog.areas.some((area) => area.key === fields.area) &&
    isCanonicalAction(fields.acao, catalog.acoesCanonicas) && Boolean(fields.publicacao.trim()) &&
    fields.publicacao.length <= MAX_PUBLICATION_CHARACTERS && fields.orientacoes.length <= MAX_GUIDANCE_CHARACTERS &&
    CASE_FIELDS.every(({ key, limit }) => fields[key].length <= limit) && validRepresentation &&
    fields.acordaoPrincipal.length <= MAX_PUBLICATION_CHARACTERS &&
    (!fields.naturezaDecisao || DECISION_TYPES.some(({ value }) => value === fields.naturezaDecisao)) &&
    (isClaude || validTemperature && validTopP) && generationReady
  const changedSinceResult = result && (JSON.stringify(caseFields) !== JSON.stringify(Object.fromEntries(Object.entries(result.input).filter(([key]) => key !== 'pesquisaJurisprudencialId'))) || juditBlocked)
  const blocker = useBlocker(({ currentLocation, nextLocation }) => hasWork &&
    (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search ||
      currentLocation.hash !== nextLocation.hash))

  useEffect(() => () => { operation.current?.abort() }, [])
  useEffect(() => {
    const controller = new AbortController()
    PecasAcervo.generationProviders(controller.signal).then((response) => {
      if (controller.signal.aborted) return
      if (response.success && Array.isArray(response.data?.provedores)) setProviders(response.data.provedores)
      else setProviderError('Não foi possível consultar a disponibilidade dos modelos externos.')
    }).catch(() => { if (!controller.signal.aborted) setProviderError('A disponibilidade do Claude não foi confirmada.') })
    return () => controller.abort()
  }, [])
  useEffect(() => {
    if (!hasWork) return undefined
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = '' }
    const beforeLogout = (event) => {
      if (typeof event.detail?.proceed !== 'function') return
      event.preventDefault()
      setPendingExit({ proceed: event.detail.proceed })
    }
    window.addEventListener('beforeunload', beforeUnload)
    window.addEventListener('auth:before-logout', beforeLogout)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      window.removeEventListener('auth:before-logout', beforeLogout)
    }
  }, [hasWork])

  const update = (key, value) => {
    setFields((current) => ({ ...current, [key]: value }))
    setError('')
    setCopyNotice('')
  }
  const cancelGeneration = () => {
    operation.current?.abort()
    operation.current = null
    setBusy(false)
    if (isClaude) setCostUncertain(true)
    setError('Geração cancelada. Os dados preenchidos foram mantidos.')
  }
  const generate = async (event) => {
    event.preventDefault()
    if (operation.current || !valid || catalog.loading || catalog.error || research.phase || juditBlocked || (!hasJudit && useResearch && !research.canUse)) return
    const controller = new AbortController()
    operation.current = controller
    const input = { ...caseFields, ...(!hasJudit && useResearch ? { pesquisaJurisprudencialId: research.job.researchId } : {}) }
    setAttemptInput(input)
    setBusy(true)
    setGenerationAudit(null)
    setCostUncertain(false)
    setError('')
    setCopyNotice('')
    try {
      const response = await PecasAcervo.generate(input, controller.signal)
      if (controller.signal.aborted) return
      setGenerationAudit(response.success ? response.data?.geracao || null : response.geracao || null)
      if (!response.success) {
        if (isClaude && !response.geracao && (!response.status || response.status >= 500)) setCostUncertain(true)
        setError(response.status === 429
          ? 'A PanaceIA está ocupada com outra indexação ou geração. Aguarde a conclusão e tente novamente.'
          : response.error || 'Não foi possível gerar o texto. Tente novamente.')
        return
      }
      if (!response.data?.texto?.trim()) {
        setError('A PanaceIA não retornou um texto para este teste. Tente novamente.')
        return
      }
      setResult({ ...response.data, fontes: response.data.fontes || [], input,
        areaLabel: areaLabel(catalog.areas, input.area), actionLabel: actionLabel(input.acao, catalog.acoesCanonicas) })
    } catch {
      if (isClaude) setCostUncertain(true)
      if (!controller.signal.aborted) setError('Não foi possível concluir a geração. Os dados preenchidos foram mantidos.')
    } finally {
      if (operation.current === controller) { operation.current = null; setBusy(false) }
    }
  }
  const chooseProvider = (provider) => {
    const configuration = providers.find((item) => item.provider === provider)
    setFields((current) => ({ ...current, provedorGeracao: provider,
      modeloGeracao: provider === 'claude' ? configuration?.defaultModel || '' : '' }))
    setError('')
  }
  const exportResult = () => {
    const blob = new Blob([JSON.stringify({ resultado: result,
      ultimaTentativa: { input: attemptInput, geracao: generationAudit, custoIncerto: costUncertain, erro: error || null } }, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `panaceia-resultado-${new Date().toISOString().replace(/[:.]/g, '-')}.private.json`
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.texto)
      setCopyNotice('Texto copiado.')
    } catch { setCopyNotice('Não foi possível copiar automaticamente. Selecione o texto do resultado para copiar.') }
  }
  const cancelExit = () => {
    setPendingExit(null)
    if (blocker.state === 'blocked') blocker.reset()
  }
  const confirmExit = () => {
    const proceed = blocker.state === 'blocked' ? blocker.proceed : pendingExit?.proceed
    operation.current?.abort()
    operation.current = null
    setBusy(false)
    setPendingExit(null)
    proceed?.()
  }

  return (
    <section className="acervo-section panaceia-test" aria-label="Testar PanaceIA">
      <header className="acervo-section-heading">
        <h2>Testar PanaceIA</h2>
        <p>Informe os dados do caso e gere a minuta da ação selecionada. As referências do escritório são opcionais.</p>
      </header>
      <div className="panaceia-test-grid">
        <form className="acervo-panel panaceia-test-form" onSubmit={generate} aria-busy={busy}>
          <h3>Contexto para a minuta</h3>
          {catalog.error && <div className="acervo-notice acervo-error" role="alert">{catalog.error}
            <button type="button" disabled={catalog.loading || busy} onClick={() => setCatalogRevision((value) => value + 1)}>Tentar novamente</button>
          </div>}
          <fieldset className="acervo-fields" disabled={busy}>
            <label className="acervo-field">Área jurídica
              <select value={fields.area} onChange={(event) => update('area', event.target.value)} required disabled={catalog.loading}>
                <option value="">{catalog.loading ? 'Carregando áreas…' : 'Selecione a área'}</option>
                {catalog.areas.map((area) => <option key={area.key} value={area.key}>{area.label}</option>)}
              </select>
            </label>
            <label className="acervo-field">Ação
              <select value={fields.acao} onChange={(event) => update('acao', event.target.value)} required disabled={catalog.loading}>
                <option value="">{catalog.loading ? 'Carregando ações…' : 'Selecione a ação'}</option>
                {catalog.acoesCanonicas.map((action) => <option key={action.codigo} value={action.codigo}>{action.nome}</option>)}
              </select>
            </label>
            {CASE_FIELDS.map(({ key, label, limit }) => <label key={key} className="acervo-field">{label} (opcional)
              <input type="text" value={fields[key]} onChange={(event) => update(key, event.target.value)}
                aria-invalid={fields[key].length > limit || ((key === 'advogadoResponsavel' || key === 'oabResponsavel') && !validRepresentation)}
                aria-describedby={`panaceia-${key}-limit${key === 'advogadoResponsavel' || key === 'oabResponsavel' ? ' panaceia-representation-help' : ''}`} />
              <small id={`panaceia-${key}-limit`}>{fields[key].length.toLocaleString('pt-BR')} / {limit.toLocaleString('pt-BR')} caracteres</small>
              {fields[key].length > limit && <small className="acervo-error" role="alert">Reduza este campo para até {limit} caracteres. O conteúdo preenchido foi preservado.</small>}
            </label>)}
            <p id="panaceia-representation-help" className="acervo-muted acervo-field-wide">Informe advogado e OAB juntos, ou deixe ambos vazios para usar os dados identificados na publicação. Preencha o polo somente quando estiver confirmado.</p>
            {!validRepresentation && <p className="acervo-error acervo-field-wide" role="alert">Preencha o advogado responsável e sua OAB, ou deixe ambos vazios.</p>}
            <label className="acervo-field acervo-field-wide">Texto da publicação / contexto do processo
              <textarea className="panaceia-publication" value={fields.publicacao}
                onChange={(event) => update('publicacao', event.target.value)} rows={13}
                required placeholder="Cole aqui o texto da publicação…"
                aria-invalid={fields.publicacao.length > MAX_PUBLICATION_CHARACTERS}
                aria-describedby="panaceia-publication-limit" />
              <small id="panaceia-publication-limit">{fields.publicacao.length.toLocaleString('pt-BR')} / {MAX_PUBLICATION_CHARACTERS.toLocaleString('pt-BR')} caracteres</small>
              {fields.publicacao.length > MAX_PUBLICATION_CHARACTERS && <small className="acervo-error" role="alert">O texto excede o limite de 10.000 caracteres. Reduza a publicação para gerar a minuta; o conteúdo colado foi preservado.</small>}
            </label>
            <h4 className="acervo-field-wide">Decisão recorrida ou relacionada (quando houver)</h4>
            <label className="acervo-field acervo-field-wide">Natureza da decisão informada (opcional)
              <select value={fields.naturezaDecisao} onChange={(event) => update('naturezaDecisao', event.target.value)}
                aria-describedby="panaceia-decision-type-help">
                <option value="">Natureza não informada</option>
                {DECISION_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
              <small id="panaceia-decision-type-help">Identifique a decisão contida na publicação, quando houver. Essa informação descreve apenas o documento e não altera a ação selecionada.</small>
            </label>
            <label className="acervo-field acervo-field-wide">Decisão / acórdão principal (opcional)
              <textarea value={fields.acordaoPrincipal} onChange={(event) => update('acordaoPrincipal', event.target.value)} rows={6}
                placeholder="Quando houver, cole o texto original da decisão ou do acórdão principal relacionado à peça."
                aria-invalid={fields.acordaoPrincipal.length > MAX_PUBLICATION_CHARACTERS}
                aria-describedby="panaceia-main-decision-help panaceia-main-decision-limit" />
              <small id="panaceia-main-decision-help">Preencha apenas se houver decisão relacionada. O julgamento dos embargos não substitui o acórdão principal ausente.</small>
              <small id="panaceia-main-decision-limit">{fields.acordaoPrincipal.length.toLocaleString('pt-BR')} / {MAX_PUBLICATION_CHARACTERS.toLocaleString('pt-BR')} caracteres</small>
              {fields.acordaoPrincipal.length > MAX_PUBLICATION_CHARACTERS && <small className="acervo-error" role="alert">A decisão excede o limite de 10.000 caracteres. Reduza o texto para gerar a minuta; o conteúdo colado foi preservado.</small>}
            </label>
            <label className="acervo-field acervo-field-wide">Orientações para a minuta (opcional)
              <textarea value={fields.orientacoes} onChange={(event) => update('orientacoes', event.target.value)} rows={3}
                placeholder="Indique a tese do cliente, os pontos a impugnar e os pedidos pretendidos, separados da posição do Tribunal."
                aria-invalid={fields.orientacoes.length > MAX_GUIDANCE_CHARACTERS}
                aria-describedby="panaceia-guidance-limit" />
              <small id="panaceia-guidance-limit">{fields.orientacoes.length.toLocaleString('pt-BR')} / {MAX_GUIDANCE_CHARACTERS.toLocaleString('pt-BR')} caracteres</small>
              {fields.orientacoes.length > MAX_GUIDANCE_CHARACTERS && <small className="acervo-error" role="alert">As orientações excedem o limite de 2.000 caracteres. Reduza o texto para gerar a minuta; o conteúdo colado foi preservado.</small>}
            </label>
          </fieldset>
          <fieldset className="acervo-fields" disabled={busy}>
            <label className="acervo-field">Gerar com
              <select value={fields.provedorGeracao} onChange={(event) => chooseProvider(event.target.value)}>
                <option value="ollama">PanaceIA · Ollama local</option>
                <option value="claude" disabled={!providers.some((provider) => provider.provider === 'claude' && provider.available)}>Claude · API Anthropic</option>
              </select>
            </label>
            {isClaude && <label className="acervo-field">Modelo Claude
              <select value={fields.modeloGeracao} onChange={(event) => update('modeloGeracao', event.target.value)} required>
                <option value="">Selecione o modelo</option>
                {(selectedProvider?.allowedModels || []).map((model) => <option value={model} key={model}>{model}</option>)}
              </select>
            </label>}
          </fieldset>
          {providerError && <p className="acervo-notice" role="status">{providerError}</p>}
          {!providerError && !providers.some((provider) => provider.provider === 'claude' && provider.available) && <p className="acervo-muted">Claude indisponível. A configuração protegida do backend precisa estar habilitada com a chave cadastrada.</p>}
          {isClaude && <p className="acervo-notice">Ao gerar com Claude, a publicação, os dados do caso e as fontes selecionadas serão enviados à Anthropic. A chamada tem cobrança em USD; o consumo e a estimativa de custo aparecerão no retorno.</p>}
          {!isClaude && <section className="panaceia-generation-parameters" aria-labelledby="panaceia-generation-parameters-title">
            <h4 id="panaceia-generation-parameters-title">Parâmetros de geração</h4>
            <fieldset className="acervo-fields" disabled={busy}>
              <label className="acervo-field">Temperatura
                <input type="number" min="0" max="2" step="0.01" required value={fields.temperatura}
                  onChange={(event) => update('temperatura', event.target.value)}
                  aria-invalid={!validTemperature} aria-describedby="panaceia-temperature-help" />
                <small id="panaceia-temperature-help">Valores maiores aumentam a variação da redação.</small>
                {!validTemperature && <small className="acervo-error" role="alert">Informe um valor entre 0 e 2.</small>}
              </label>
              <label className="acervo-field">Top P
                <input type="number" min="0.01" max="1" step="0.01" required value={fields.topP}
                  onChange={(event) => update('topP', event.target.value)}
                  aria-invalid={!validTopP} aria-describedby="panaceia-top-p-help" />
                <small id="panaceia-top-p-help">Valores menores restringem as opções de palavras.</small>
                {!validTopP && <small className="acervo-error" role="alert">Informe um valor entre 0,01 e 1.</small>}
              </label>
            </fieldset>
          </section>}
          <JuditDocumentsPanel judit={judit} enabled={useDocuments} onEnabledChange={setUseDocuments}
            processNumber={process.numeroProcesso} publication={fields.publicacao}
            disabled={busy || Boolean(research.phase) || research.pending} />
          {useDocuments && process.error && <p className="acervo-error" role="alert">{process.error}</p>}
          <p className="acervo-muted">As referências do escritório orientam a redação. Revise os dados do caso, os pedidos e os alertas apresentados antes de finalizar.</p>
          {error && <p className="acervo-notice acervo-error" role="alert">{error}</p>}
          <div className="acervo-toolbar-actions">
            <button type="submit" className="acervo-button acervo-primary" disabled={busy || !valid || catalog.loading || Boolean(catalog.error) || Boolean(research.phase) || juditBlocked || (!hasJudit && useResearch && !research.canUse)}>
              <i className={`fas ${busy ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'}`} aria-hidden="true" />
              {busy ? `Gerando com ${isClaude ? 'Claude' : 'PanaceIA'}…` : `Gerar com ${isClaude ? 'Claude' : 'PanaceIA'}`}
            </button>
            {busy && <button type="button" className="acervo-button" onClick={cancelGeneration}>Cancelar</button>}
          </div>
          {busy && <p className="acervo-working" role="status">Consultando as referências e gerando a minuta. Isso pode levar alguns minutos.</p>}
          {!hasJudit && useResearch && !research.canUse && <p className="acervo-notice" role="status">Confira novamente a pesquisa e autorize uma referência, ou desmarque seu uso para gerar sem jurisprudência desta pesquisa.</p>}
          {juditBlocked && <p className="acervo-notice" role="status">Conclua a operação e confira a consulta e os documentos da Judit antes de gerar. {judit.selectionError}</p>}
        </form>
        <section className="acervo-panel panaceia-test-result" aria-label="Resultado da PanaceIA">
          <div className="acervo-toolbar">
            <h3>Resultado da PanaceIA</h3>
            {result && <button type="button" className="acervo-button" onClick={copy}><i className="far fa-copy" aria-hidden="true" /> Copiar texto</button>}
            {(result || generationAudit || costUncertain) && <button type="button" className="acervo-button" disabled={busy} onClick={exportResult}>Exportar resultado e consumo</button>}
          </div>
          {copyNotice && <p className="acervo-notice" role="status">{copyNotice}</p>}
          {(generationAudit || costUncertain) && <div><p className="acervo-muted">Consumo da última tentativa de geração</p><DraftGenerationUsage audit={generationAudit} uncertain={costUncertain} /></div>}
          {result ? <>
            <p className="panaceia-result-classification">{result.areaLabel} · {result.actionLabel}</p>
            <p className="acervo-notice">Minuta para revisão. O resultado deste teste não é salvo automaticamente.</p>
            {result.aviso && <p className="acervo-notice panaceia-reference-warning" role="status"><i className="fas fa-triangle-exclamation" aria-hidden="true" /> {result.aviso}</p>}
            {result.alertasJurisprudencia?.map((alert, index) => <p key={index} className="acervo-notice panaceia-reference-warning" role="status">{researchWarning(alert)}</p>)}
            {(changedSinceResult || busy || error) && <p className="acervo-muted" role="status">{error ? 'A última tentativa não foi concluída. ' : ''}O texto abaixo é da última geração concluída{changedSinceResult ? ', anterior às alterações do formulário' : ''}.</p>}
            <div className="panaceia-generated-text" tabIndex={0} aria-label="Texto gerado pela PanaceIA">{result.texto}</div>
            <JuditDocumentSources sources={result.fontesJudit || []} />
            {result.fontes.length > 0 && <div className="panaceia-sources">
              <h4>Referências de escrita consultadas ({result.fontes.length})</h4>
              <p className="acervo-muted">Exemplos de linguagem e organização; não comprovam fatos ou fundamentos deste processo.</p>
              <ul>{result.fontes.map((source, index) => <li key={`${source.id}-${index}`}>
                <strong>{source.titulo}</strong>
                <span>{source.tituloSecao || 'Trecho da peça'} · Fragmento {source.sequencia}</span>
              </li>)}</ul>
            </div>}
            {result.jurisprudenciasConferidas?.length > 0 && <div className="panaceia-sources">
              <h4>Jurisprudências conferidas usadas na minuta</h4>
              <ul>{result.jurisprudenciasConferidas.map((reference) => <li key={reference.id}>
                <strong>{reference.tribunal}{reference.processo ? ` · ${reference.processo}` : ''}</strong>
                <span>{reference.trecho}</span>
              </li>)}</ul>
            </div>}
          </> : <div className="acervo-empty panaceia-result-empty" role="status">
            <i className={`fas ${busy ? 'fa-spinner fa-spin' : 'fa-pen-nib'}`} aria-hidden="true" />
            <h3>{busy ? 'Preparando a minuta…' : 'Sua minuta aparecerá aqui'}</h3>
            <p>{busy ? 'Aguarde a resposta do modelo selecionado.' : 'Selecione a área e a ação, informe a publicação e gere a minuta com o modelo escolhido.'}</p>
          </div>}
        </section>
      </div>
      {hasJudit && <p className="acervo-notice">A combinação de documentos da Judit com pesquisa de jurisprudência está desabilitada neste fluxo. As orientações da minuta continuam disponíveis para confirmar a posição e o objetivo atuais do cliente.</p>}
      <LegalResearchPanel research={research} valid={valid && !catalog.loading && !catalog.error && !useDocuments} disabled={busy || useDocuments}
        useResearch={useResearch} onUseResearch={setUseResearch} />
      <Modal isOpen={Boolean(pendingExit) || blocker.state === 'blocked'} onClose={cancelExit}
        title="Sair do teste da PanaceIA?" size="small"
        footer={<><button className="btn btn-outline" onClick={cancelExit}>Continuar no teste</button><button className="btn btn-primary" onClick={confirmExit}>Sair do teste</button></>}>
        <p>Os dados preenchidos, os textos, os parâmetros e o resultado da minuta ficam somente nesta tela e serão descartados ao sair.{busy ? ' A geração em andamento será cancelada.' : ' Copie o resultado antes de sair se quiser guardá-lo.'}
          {research.job ? ' A pesquisa já recebida pelo servidor é mantida conforme sua retenção. Cancele a pesquisa antes de sair se quiser interromper seu processamento.' : ''}
          {judit.hasWork ? ' As consultas e os documentos salvos da Judit permanecem disponíveis pelo processo. A seleção de páginas desta tela será descartada.' : ''}</p>
      </Modal>
    </section>
  )
}
